import { dom } from "@omegagrid/core";


export type DependencyType = 'js'|'css';
export type Dependency = {url: string, name?: string, type?: DependencyType};

export class DependencyLoaderEvent extends Event {
	dependency: Dependency;
	constructor(type: string, args?: Partial<DependencyLoaderEvent>) {
		super(type);
		if (args) Object.assign(this, args);
	}
}

export class DependencyLoader extends EventTarget {

	constructor(private parentElement: HTMLHeadElement|HTMLBodyElement) {
		super();
	}

	async load(urls: (string|Dependency)[]): Promise<void> {
		const scripts: Dependency[] = [];
		const styles: Dependency[] = [];
		
		urls.forEach(item => {
			const u = typeof item === 'string' ? {url: item, type: null} : item;
			if (!u.url.startsWith('http:') && !u.url.startsWith('https:')) {
				u.url = new URL(u.url, window.location.origin).toString();
			}
			const url = new URL(u.url);
			if (u.type == 'js' || url.pathname.endsWith('.js')) scripts.push(u);
			else if (u.type == 'css' || url.pathname.endsWith('.css')) styles.push(u);
		});

		await Promise.all([
			...styles.map(url => this.loadStyle(url)),
			...scripts.map(src => this.loadScript(src))
		]);
	}

	loadStyle(dependency: string|Dependency): Promise<void> {
		dependency = typeof dependency === 'string' ? {url: dependency} : dependency;
		dependency.type = 'css';
		return new Promise((resolve, reject) => {
			const link = dom.createElement<HTMLLinkElement>('link', {
				rel: 'stylesheet',
				href: dependency.url,
				type: 'text/css',
				listeners: [
					['load', () => {
						this.dispatchEvent(new DependencyLoaderEvent('load', { dependency }));
						resolve();
					}],
					['error', () => {
						this.dispatchEvent(new DependencyLoaderEvent('error', { dependency }));
						reject(new Error(`Failed to load style: ${dependency.url}`));
					}]
				]
			});
			this.parentElement.appendChild(link);
		});
	}

	loadScript(dependency: string|Dependency): Promise<void> {
		dependency = typeof dependency === 'string' ? {url: dependency} : dependency;
		dependency.type = 'js';
		return new Promise((resolve, reject) => {
			this.parentElement.appendChild(dom.createElement<HTMLScriptElement>('script', {
				type: 'text/javascript',
				async: false,
				src: dependency.url,
				listeners: [
					['load', () => {
						this.dispatchEvent(new DependencyLoaderEvent('load', { dependency }));
						resolve();
					}],
					['error', () => {
						this.dispatchEvent(new DependencyLoaderEvent('error', { dependency }));
						reject(new Error(`Failed to load script: ${dependency.url}`));
					}]
				]
			}));
		});
	}

}
